import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';

interface ModernButtonProps {
  children: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'accent' | 'glass' | 'gradient' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  shape?: 'rounded' | 'pill' | 'square';
  effect?: 'shimmer' | 'glow' | 'float' | 'pulse' | 'none';
  loading?: boolean;
  disabled?: boolean;
  icon?: React.ComponentType<any>;
  iconPosition?: 'left' | 'right';
  onClick?: () => void;
  className?: string;
  gradient?: 'primary' | 'secondary' | 'accent' | 'rainbow' | 'sunset' | 'ocean';
  fullWidth?: boolean;
}

const ModernButton: React.FC<ModernButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  shape = 'rounded',
  effect = 'shimmer',
  loading = false,
  disabled = false,
  icon: Icon,
  iconPosition = 'left',
  onClick,
  className = '',
  gradient = 'primary',
  fullWidth = false
}) => {
  const [isPressed, setIsPressed] = useState(false);

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'px-4 py-2 text-sm';
      case 'md':
        return 'px-6 py-3 text-base';
      case 'lg':
        return 'px-8 py-4 text-lg';
      case 'xl':
        return 'px-10 py-5 text-xl';
      default:
        return 'px-6 py-3 text-base';
    }
  };

  const getShapeClasses = () => {
    switch (shape) {
      case 'pill':
        return 'rounded-full';
      case 'square':
        return 'rounded-lg';
      default:
        return 'rounded-xl';
    }
  };

  const getVariantClasses = () => {
    switch (variant) {
      case 'primary':
        return 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0';
      case 'secondary':
        return 'bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white border-0';
      case 'accent':
        return 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-0';
      case 'glass':
        return 'bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20';
      case 'gradient':
        return getGradientClasses();
      case 'outline':
        return 'bg-transparent border-2 border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white';
      case 'ghost':
        return 'bg-transparent text-blue-500 hover:bg-blue-50';
      default:
        return 'bg-blue-500 hover:bg-blue-600 text-white border-0';
    }
  };

  const getGradientClasses = () => {
    switch (gradient) {
      case 'primary':
        return 'bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0';
      case 'secondary':
        return 'bg-gradient-to-r from-purple-500 to-pink-600 text-white border-0';
      case 'accent':
        return 'bg-gradient-to-r from-green-500 to-teal-600 text-white border-0';
      case 'rainbow':
        return 'bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 to-purple-600 text-white border-0';
      case 'sunset':
        return 'bg-gradient-to-r from-orange-400 to-pink-500 text-white border-0';
      case 'ocean':
        return 'bg-gradient-to-r from-cyan-400 to-blue-600 text-white border-0';
      default:
        return 'bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0';
    }
  };

  const getEffectClasses = () => {
    if (disabled || loading) return '';
    
    switch (effect) {
      case 'shimmer':
        return 'relative overflow-hidden';
      case 'glow':
        return 'hover-glow';
      case 'float':
        return 'animate-float';
      case 'pulse':
        return 'animate-pulse-glow';
      default:
        return '';
    }
  };

  const buttonClasses = `
    ${getSizeClasses()}
    ${getShapeClasses()}
    ${getVariantClasses()}
    ${getEffectClasses()}
    ${fullWidth ? 'w-full' : ''}
    ${disabled || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    font-semibold transition-all duration-300 ease-in-out
    transform active:scale-95
    ${className}
  `.trim();

  const handleClick = () => {
    if (!disabled && !loading && onClick) {
      onClick();
    }
  };

  const handleMouseDown = () => {
    if (!disabled && !loading) {
      setIsPressed(true);
    }
  };

  const handleMouseUp = () => {
    setIsPressed(false);
  };

  const handleMouseLeave = () => {
    setIsPressed(false);
  };

  return (
    <button
      className={buttonClasses}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      disabled={disabled || loading}
      style={{
        transform: isPressed ? 'scale(0.95)' : 'scale(1)',
        transition: 'transform 0.1s ease-in-out'
      }}
    >
      {/* Shimmer Effect */}
      {effect === 'shimmer' && !disabled && !loading && (
        <div className="absolute inset-0 -top-1 -bottom-1 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 -translate-x-full hover:translate-x-full transition-transform duration-1000 ease-out"></div>
      )}

      {/* Content */}
      <div className="relative flex items-center justify-center gap-2">
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            {Icon && iconPosition === 'left' && (
              <Icon className="w-5 h-5" />
            )}
            <span>{children}</span>
            {Icon && iconPosition === 'right' && (
              <Icon className="w-5 h-5" />
            )}
          </>
        )}
      </div>
    </button>
  );
};

export default ModernButton;
